import { api, ApiError } from "@/api/client";
import type { Paper, PaperStatus } from "@/api/types";

const DATABASE = "dailyresearch-actions-v3";
const STORE = "operations";

export interface PendingAction {
  key: string;
  user: string;
  action_id: string;
  paper: Paper;
  status: PaperStatus;
  created_at: number;
}

export function actionKey(user: string, actionId: string): string {
  if (!user || !actionId) throw new Error("user and action id are required");
  return `${encodeURIComponent(user)}::${actionId}`;
}

export function makePendingAction(
  user: string,
  paper: Paper,
  status: PaperStatus,
  actionId: string,
  now = Date.now(),
): PendingAction {
  return {
    key: actionKey(user, actionId),
    user,
    action_id: actionId,
    paper,
    status,
    created_at: now,
  };
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE)) {
        const store = database.createObjectStore(STORE, { keyPath: "key" });
        store.createIndex("user", "user", { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("无法打开离线队列"));
  });
}

async function transaction<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE, mode);
    const request = run(tx.objectStore(STORE));
    tx.oncomplete = () => { database.close(); resolve(request.result); };
    tx.onerror = () => { database.close(); reject(tx.error ?? new Error("离线队列操作失败")); };
    tx.onabort = () => { database.close(); reject(tx.error ?? new Error("离线队列操作中止")); };
  });
}

export async function enqueueAction(action: PendingAction): Promise<void> {
  await transaction("readwrite", (store) => store.put(action));
}

export async function deleteAction(key: string): Promise<void> {
  await transaction("readwrite", (store) => store.delete(key));
}

export async function listActions(user: string): Promise<PendingAction[]> {
  const result = await transaction("readonly", (store) => store.index("user").getAll(user));
  return (result as PendingAction[]).sort((left, right) => left.created_at - right.created_at);
}

export function isOfflineFailure(error: unknown): boolean {
  return !navigator.onLine || error instanceof TypeError
    || (error instanceof ApiError && error.status === 0);
}

export async function flushActions(user: string): Promise<number> {
  if (!navigator.onLine) return 0;
  let flushed = 0;
  for (const action of await listActions(user)) {
    try {
      await api.updateStatus(action.paper, action.status, action.action_id);
      await deleteAction(action.key);
      flushed += 1;
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        await deleteAction(action.key);
        continue;
      }
      break;
    }
  }
  return flushed;
}
